// Offline mode: the page's link to the offline engine that the host app (the Android wrapper) may run next to this POS.
//
// The engine is reached through the SAME base URL as the API (the loopback server, e.g. http://127.0.0.1:8765):
//   GET  <base>/_oa/status                  engine state + device registration + staff credential figures
//   GET  <base>/_oa/parked                  records the server refused to take
//   POST <base>/_oa/register {pin,name?} | reissue {pin} | unregister {pin?} | refresh | retry-upload | retry {uid} | discard {uid}
//   GET  <base>/_offline/state              (opt-in control route) 200 when the host allows the page to flip the offline switch
//   POST <base>/_offline/forced {value}     (same opt-in) manual offline on / off
// plus the `X-OA-Mode` response header every loopback response carries (a cheap "mode changed" signal).
//
// With no engine the POS is unchanged: the monitor never starts unless the API base is a loopback address (so the production API
// never sees a probe), a 404 / non-engine answer ends all polling, and every call below answers "not available" without throwing.

import { normalizeExtras, normalizeState, looksLikeEngineStatus, ONLINE_STATE } from './state';

export const MODES = ['online', 'offline', 'forced_offline'];
export const NO_ENGINE = Object.freeze({ ok: false, message: 'Offline mode is not available on this device.' });

const EMPTY_EXTRAS = normalizeExtras(null);
const trim = (b) => String(b || '').replace(/\/+$/, '');

/** Only a loopback address can be an on-device engine. Anything else (the production API, a LAN dev server) is never probed. */
export function isLoopbackBase(base) {
  try {
    const h = new URL(base).hostname;
    return h === '127.0.0.1' || h === 'localhost' || h === '[::1]' || h === '::1';
  } catch {
    return false;
  }
}

/** { ok, message, ... } from a control answer; anything unreadable is a failure, never an exception. */
export function parseControl(text) {
  try {
    const d = JSON.parse(text);
    return d && typeof d === 'object' && !Array.isArray(d) ? d : { ok: false, message: 'Unreadable answer.' };
  } catch {
    return { ok: false, message: 'Unreadable answer.' };
  }
}

/**
 * The engine connection: current state (a stable object that changes only when the engine's answer does), detection of whether an
 * engine exists at all, polling (every pollMs; backs off after failures; stops for good when the base answers but is not an engine),
 * and the control calls. Timers and fetch are injectable so it can be tested without a browser.
 */
export function createConnection({
  baseUrl,
  fetchImpl = typeof fetch === 'function' ? (...args) => fetch(...args) : null,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (t) => clearTimeout(t),
  now = () => Date.now(),
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  pollMs = 7000,
  offlinePollMs = 3000,
  giveUpAfter = 4, // consecutive "unreachable" probes before a never-seen engine is written off
  retryGivenUpAfterMs = 60000, // a focus event may probe again, but not more often than this
} = {}) {
  const base = trim(baseUrl);
  // the opt-in /_offline/* control routes live at the root of the loopback server, whatever path the API base carries
  const root = (() => {
    try {
      return new URL(base).origin;
    } catch {
      return base;
    }
  })();
  const listeners = new Set();
  let snap = Object.freeze({ state: ONLINE_STATE, device: EMPTY_EXTRAS.device, staff: EMPTY_EXTRAS.staff, engine: false, canForce: false, headerOnly: false });
  let engine = false; // a valid /_oa/status has been seen
  let started = false;
  let gaveUp = false;
  let failures = 0;
  let timer = null;
  let inflight = null;
  let lastProbeAt = 0;
  let forceProbed = false;

  const sig = (s) => JSON.stringify([s.state, s.device, s.staff, s.engine, s.canForce, s.headerOnly]);
  function publish(next) {
    const merged = { ...snap, ...next };
    if (sig(merged) === sig(snap)) return;
    snap = Object.freeze(merged);
    listeners.forEach((fn) => fn());
  }

  async function request(method, path, { body, timeoutMs = 4000, atRoot = false } = {}) {
    if (!fetchImpl) return { status: 0, json: null, text: '', mode: null };
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const t = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
    try {
      const res = await fetchImpl(`${atRoot ? root : base}${path}`, {
        method,
        cache: 'no-store',
        ...(body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
        ...(ctl ? { signal: ctl.signal } : {}),
      });
      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch {
        /* not JSON */
      }
      return { status: res.status, json, text, mode: res.headers?.get?.('X-OA-Mode') || null };
    } catch {
      return { status: 0, json: null, text: '', mode: null };
    } finally {
      if (t) clearTimeout(t);
    }
  }

  async function probeForce() {
    forceProbed = true;
    const r = await request('GET', '/_offline/state', { atRoot: true });
    publish({ canForce: r.status === 200 && looksLikeEngineStatus(r.json) });
  }

  function poll() {
    if (inflight) return inflight;
    lastProbeAt = now();
    inflight = (async () => {
      const r = await request('GET', '/_oa/status');
      if (r.status === 200 && looksLikeEngineStatus(r.json)) {
        engine = true;
        gaveUp = false;
        failures = 0;
        publish({ state: normalizeState(r.json), ...normalizeExtras(r.json), engine: true, headerOnly: false });
        if (!forceProbed) await probeForce();
      } else if (r.status === 0) {
        failures += 1; // unreachable / timed out
        if (!engine && failures >= giveUpAfter) gaveUp = true;
      } else if (!engine) {
        gaveUp = true; // answered, but not by an engine (the cloud's 404, a captive page): no engine here
      } else {
        failures += 1; // an engine that answered once now answers something else: keep the last state and keep trying
      }
    })().finally(() => {
      inflight = null;
    });
    return inflight;
  }

  function schedule() {
    clearTimer(timer);
    timer = null;
    if (!started || gaveUp) return;
    // while offline the counts (orders waiting to upload) move as orders are taken: read them a little more often
    const every = snap.state.mode !== 'online' ? Math.min(pollMs, offlinePollMs) : pollMs;
    const delay = failures > 0 ? Math.min(pollMs * 2 ** failures, engine ? 30000 : 300000) : every;
    timer = setTimer(async () => {
      await poll();
      schedule();
    }, delay);
  }

  /** Reads the state now (the page was focused, a control call finished, or the mode header changed) and reschedules. */
  async function refresh() {
    if (!started) return snap;
    if (gaveUp && now() - lastProbeAt < retryGivenUpAfterMs) return snap;
    clearTimer(timer);
    await poll();
    schedule();
    return snap;
  }

  return {
    getSnapshot: () => snap,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    /** Starts monitoring. A no-op (false) unless the API base is a loopback address and fetch exists. */
    start() {
      if (started) return true;
      if (!isLoopbackBase(base) || !fetchImpl) return false;
      started = true;
      poll().then(schedule);
      return true;
    },
    stop() {
      started = false;
      clearTimer(timer);
      timer = null;
    },
    refresh,
    hasEngine: () => engine,
    /** The X-OA-Mode header of a response the page got anyway. A change refreshes the whole state; with no /_oa/status the header alone drives the mode. */
    noteMode(mode) {
      if (!started || !MODES.includes(mode)) return;
      if (engine) {
        if (mode !== snap.state.mode) refresh();
      } else if (mode === 'online') {
        if (snap.headerOnly) publish({ state: ONLINE_STATE, headerOnly: false });
      } else {
        publish({ state: normalizeState({ mode }), headerOnly: true });
      }
    },
    /** Control call under /_oa/: { ok, message, ... }, never throws. */
    async call(method, action, body) {
      if (!engine) return NO_ENGINE;
      const r = await request(method, `/_oa/${action}`, { body: method === 'POST' ? body || {} : undefined, timeoutMs: 90000 });
      const out = r.status === 200 ? parseControl(r.text) : { ok: false, message: r.status === 0 ? 'The offline engine did not answer.' : 'The request failed.' };
      if (method === 'POST') await refresh();
      return out;
    },
    async listParked() {
      const r = await this.call('GET', 'parked');
      return r && Array.isArray(r.parked) ? { parked: r.parked, flagged: Array.isArray(r.flagged) ? r.flagged : [] } : { parked: [], flagged: [] };
    },
    /** Manual offline switch (opt-in loopback control route). False when the host does not expose it. */
    async setForced(on) {
      if (!engine || !snap.canForce) return false;
      const r = await request('POST', '/_offline/forced', { body: { value: Boolean(on) }, atRoot: true });
      // The host applies the switch asynchronously, so the first read can still show the old mode: read again until it shows the new one.
      for (let i = 0; i < 6; i++) {
        await refresh();
        if ((snap.state.mode === 'forced_offline') === Boolean(on)) break;
        await sleep(400);
      }
      return r.status === 200;
    },
  };
}
